import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CenteredPanel } from '@/shared/feedback/CenteredPanel.tsx'
import { AuditView } from '../AuditView/AuditView.tsx'
import { isLive, useAudit, useAuditAnomalies, useAuditStages } from '../hooks/index.ts'

/** `/audits/:auditId` — where stages run, the quota is watched and the counters are read (§10.1). */
export function AuditPage() {
  const { t } = useTranslation()
  const { auditId } = useParams<{ auditId: string }>()
  const { detail, error, isLoading, refresh } = useAudit(auditId)
  const live = detail !== null && isLive(detail.audit.state)
  const stages = useAuditStages(auditId, live)
  const anomalies = useAuditAnomalies(auditId, live)

  if (isLoading || !auditId) {
    return (
      <CenteredPanel
        title={t('pages.audits.page.loading', { defaultValue: 'Loading the audit…' })}
        muted
      />
    )
  }
  if (error || detail === null) {
    return (
      <CenteredPanel
        title={t('pages.audits.page.error.title', { defaultValue: 'Could not load the audit' })}
        body={
          error?.kind === 'not_found'
            ? t('pages.audits.page.error.notFound', { defaultValue: 'No audit with this id.' })
            : t('pages.audits.page.error.generic', {
                defaultValue: 'The engine returned an error.',
              })
        }
        meta={
          error && error.status !== null ? `HTTP ${error.status} · ${error.message}` : undefined
        }
        tone="error"
      />
    )
  }

  return (
    <AuditView
      detail={detail}
      stages={stages.stages}
      anomalies={anomalies.byKind}
      onChanged={async () => {
        await Promise.all([refresh(), stages.refresh(), anomalies.refresh()])
      }}
    />
  )
}
